import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import FollowersPopup from './FollowersPopup';
import LiveStreamBlock from './LiveStreamBlock';
import MediaGallery from './MediaGallery';
import { ROUTES, getPlatformById } from '../../constants';
import {
  authApi,
  clearStoredAuth,
  readStoredToken,
} from '../../services/authApi';
import { messageApi } from '../../services/messageApi';
import streamApi from '../../services/streamApi';
import communityApi from '../../services/communityApi';
import { readStoredUser, useNavbarUser } from '../../utils/navbarUser';
import './UserProfilePage.css';

const PER_USER_CHATS_ENABLED =
  String(process.env.REACT_APP_ENABLE_PER_USER_CHATS).toLowerCase() === 'true';

function UserProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const navbarUser = useNavbarUser({ notificationCount: 0 });
  const currentUser = readStoredUser();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followHover, setFollowHover] = useState(false);
  const [showFollowersPopup, setShowFollowersPopup] = useState(false);
  const [followersPopupTab, setFollowersPopupTab] = useState('followers');
  const [isHeroPlaying, setIsHeroPlaying] = useState(true);
  const [isHeroMuted, setIsHeroMuted] = useState(true);
  const [isHeroHovering, setIsHeroHovering] = useState(false);
  const heroVideoRef = useRef(null);
  const [liveStatus, setLiveStatus] = useState(null);

  useEffect(() => {
    if (!username) return;

    if (currentUser?.username && currentUser.username === username) {
      navigate(ROUTES.PROFILE, { replace: true });
      return;
    }

    let cancelled = false;

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const profileResponse = await authApi.getPublicProfile(username);
        if (cancelled) return;

        setProfile(profileResponse.profile || null);

        if (readStoredToken()) {
          try {
            const followResponse = await authApi.checkIsFollowing(username);
            if (!cancelled) {
              setIsFollowing(Boolean(followResponse.isFollowing));
            }
          } catch {
            if (!cancelled) {
              setIsFollowing(false);
            }
          }
        } else if (!cancelled) {
          setIsFollowing(false);
        }
      } catch (fetchError) {
        console.error('Failed to load public profile:', fetchError);
        if (!cancelled) {
          setProfile(null);
          setError(fetchError.message || 'Failed to load profile');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.username, navigate, username]);

  useEffect(() => {
    const profileId = profile?.id;
    if (!profileId) {
      setLiveStatus(null);
      return undefined;
    }

    let cancelled = false;

    const fetchLive = async () => {
      try {
        const statuses = await streamApi.getLiveStatuses([profileId]);
        if (cancelled) return;
        const entry = Array.isArray(statuses)
          ? statuses.find((s) => String(s.user_id) === String(profileId))
          : null;
        setLiveStatus(entry && entry.is_live ? entry : null);
      } catch {
        if (!cancelled) setLiveStatus(null);
      }
    };

    fetchLive();
    const interval = setInterval(fetchLive, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [profile?.id]);

  const handleLogout = useCallback(async () => {
    const token = readStoredToken();
    await authApi.logout(token);
    clearStoredAuth();
    navigate(ROUTES.WELCOME);
  }, [navigate]);

  const handleFollowToggle = async () => {
    if (!readStoredToken()) {
      navigate(ROUTES.AUTH);
      return;
    }

    try {
      const response = isFollowing
        ? await authApi.unfollowUser(username)
        : await authApi.followUser(username);

      setIsFollowing(Boolean(response.isFollowing));
      setProfile((current) => (
        current
          ? {
              ...current,
              followersCount: typeof response.followersCount === 'number'
                ? response.followersCount
                : current.followersCount,
            }
          : current
      ));
    } catch (toggleError) {
      console.error('Failed to toggle follow state:', toggleError);
    }
  };

  const handleMessage = async () => {
    if (!readStoredToken()) {
      navigate(ROUTES.AUTH);
      return;
    }

    try {
      const response = await messageApi.createConversation(username);
      navigate(`/messages?conversation=${encodeURIComponent(response.conversationId)}`);
    } catch (messageError) {
      console.error('Failed to start conversation:', messageError);
    }
  };

  const handleJoinChat = async () => {
    if (!readStoredToken()) {
      navigate(ROUTES.AUTH);
      return;
    }
    try {
      await communityApi.joinUserChat(username);
    } catch (joinError) {
      console.error('Failed to join chat:', joinError);
    }
    navigate(`/community/user/${encodeURIComponent(username)}`);
  };

  const openFollowersPopup = (tab) => {
    setFollowersPopupTab(tab);
    setShowFollowersPopup(true);
  };

  if (loading) {
    return (
      <div className="user-profile-page">
        <HomeNavBar
          spadesBalance={navbarUser.spades}
          notificationCount={navbarUser.notifications}
          userAvatar={navbarUser.avatar}
          userName={navbarUser.name}
          onSpadesClick={() => {}}
          onLogout={handleLogout}
          minimal
        />
        <main className="user-profile-page__main">
          <div className="user-profile-page__loader">
            <div className="user-profile-page__loader-ring" />
          </div>
        </main>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="user-profile-page">
        <HomeNavBar
          spadesBalance={navbarUser.spades}
          notificationCount={navbarUser.notifications}
          userAvatar={navbarUser.avatar}
          userName={navbarUser.name}
          onSpadesClick={() => {}}
          onLogout={handleLogout}
          minimal
        />
        <main className="user-profile-page__main">
          <div className="user-profile-page__not-found">
            <div className="user-profile-page__not-found-icon">?</div>
            <h2>User not found</h2>
            <p>{error || "The profile you're looking for doesn't exist."}</p>
            <button type="button" onClick={() => navigate(ROUTES.HOME)}>
              Back to Home
            </button>
          </div>
        </main>
        <Footer fullWidth />
      </div>
    );
  }

  const avatarUrl = profile.profilePictureUrl || profile.oauthProfileImage || null;
  const bannerUrl = profile.bannerUrl || null;
  const heroVideoUrl = profile.videoUrl || null;
  const platforms = profile.connectedPlatforms || [];
  const initial = profile.username ? profile.username.charAt(0).toUpperCase() : '?';
  const isOwnProfile = currentUser?.username && currentUser.username === profile.username;

  return (
    <div className="user-profile-page">
      <HomeNavBar
        spadesBalance={navbarUser.spades}
        notificationCount={navbarUser.notifications}
        userAvatar={navbarUser.avatar}
        userName={navbarUser.name}
        onSpadesClick={() => {}}
        onLogout={handleLogout}
        minimal
      />

      <main className="user-profile-page__main">
        <div
          className={`user-profile-page__hero${heroVideoUrl ? ' user-profile-page__hero--interactive' : ''}`}
          onMouseEnter={() => setIsHeroHovering(true)}
          onMouseLeave={() => setIsHeroHovering(false)}
          onClick={() => {
            if (!heroVideoRef.current) return;
            if (heroVideoRef.current.paused) {
              heroVideoRef.current.play();
            } else {
              heroVideoRef.current.pause();
            }
          }}
          role={heroVideoUrl ? 'button' : undefined}
          tabIndex={heroVideoUrl ? 0 : undefined}
          onKeyDown={(event) => {
            if (!heroVideoRef.current) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (heroVideoRef.current.paused) {
              heroVideoRef.current.play();
            } else {
              heroVideoRef.current.pause();
            }
          }}
        >
          {heroVideoUrl ? (
            <>
              <video
                ref={heroVideoRef}
                src={heroVideoUrl}
                className="user-profile-page__hero-video"
                autoPlay
                muted
                loop
                playsInline
                onPlay={() => setIsHeroPlaying(true)}
                onPause={() => setIsHeroPlaying(false)}
              />
              <button
                type="button"
                className={`user-profile-page__hero-play-btn${isHeroHovering ? ' user-profile-page__hero-play-btn--visible' : ''}`}
                aria-label={isHeroPlaying ? 'Pause intro video' : 'Play intro video'}
                tabIndex={-1}
              >
                {isHeroPlaying ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="6 3 20 12 6 21 6 3" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                className={`user-profile-page__hero-mute-btn${isHeroHovering ? ' user-profile-page__hero-mute-btn--visible' : ''}`}
                aria-label={isHeroMuted ? 'Unmute intro video' : 'Mute intro video'}
                tabIndex={-1}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!heroVideoRef.current) return;
                  heroVideoRef.current.muted = !heroVideoRef.current.muted;
                  setIsHeroMuted(heroVideoRef.current.muted);
                }}
              >
                {isHeroMuted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                  </svg>
                )}
              </button>
            </>
          ) : bannerUrl ? (
            <img src={bannerUrl} alt="" className="user-profile-page__hero-image" />
          ) : (
            <div className="user-profile-page__hero-gradient" />
          )}
          <div className="user-profile-page__hero-overlay" />
        </div>

        <section className="user-profile-page__card">
          <div className="user-profile-page__avatar-ring">
            <div className="user-profile-page__avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt={profile.username} referrerPolicy="no-referrer" />
              ) : (
                <div className="user-profile-page__avatar-fallback">
                  <span>{initial}</span>
                </div>
              )}
            </div>
          </div>

          <div className="user-profile-page__identity">
            <h1 className="user-profile-page__username">{profile.username}</h1>
            {profile.joinedDate && (
              <p className="user-profile-page__joined">Joined {profile.joinedDate}</p>
            )}
          </div>

          <div className="user-profile-page__stats">
            <button
              type="button"
              className="user-profile-page__stat"
              onClick={() => openFollowersPopup('followers')}
            >
              <span className="user-profile-page__stat-number">{profile.followersCount || 0}</span>
              <span className="user-profile-page__stat-label">Followers</span>
            </button>
            <div className="user-profile-page__stat-divider" />
            <button
              type="button"
              className="user-profile-page__stat"
              onClick={() => openFollowersPopup('following')}
            >
              <span className="user-profile-page__stat-number">{profile.followingCount || 0}</span>
              <span className="user-profile-page__stat-label">Following</span>
            </button>
          </div>

          {!isOwnProfile && (
            <div className="user-profile-page__action-buttons">
              <button
                type="button"
                className={`user-profile-page__follow-btn ${isFollowing ? 'user-profile-page__follow-btn--following' : ''}`}
                onClick={handleFollowToggle}
                onMouseEnter={() => setFollowHover(true)}
                onMouseLeave={() => setFollowHover(false)}
              >
                {isFollowing
                  ? (followHover ? 'Unfollow' : 'Following')
                  : 'Follow'}
              </button>
              {isFollowing && (
                <button
                  type="button"
                  className="user-profile-page__message-btn"
                  onClick={handleMessage}
                >
                  Message
                </button>
              )}
              {isFollowing && PER_USER_CHATS_ENABLED && (
                <button
                  type="button"
                  className="user-profile-page__message-btn"
                  onClick={handleJoinChat}
                  title={`Open @${username}'s community chat`}
                >
                  Join Community Chat
                </button>
              )}
            </div>
          )}
        </section>

        {liveStatus && (
          <LiveStreamBlock
            profile={profile}
            liveStatus={liveStatus}
          />
        )}

        <section className="user-profile-page__sections">
          {profile.bio && (
            <section className="user-profile-page__section">
              <h3 className="user-profile-page__section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                About
              </h3>
              <p className="user-profile-page__bio">{profile.bio}</p>
            </section>
          )}

          {platforms.length > 0 && (
            <section className="user-profile-page__section">
              <h3 className="user-profile-page__section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                Connected Platforms
              </h3>
              <div className="user-profile-page__platforms">
                {platforms.map((platform) => {
                  const platformMeta = getPlatformById(platform.platform);
                  return (
                    <a
                      key={platform.id || `${platform.platform}-${platform.platformUsername}`}
                      className="user-profile-page__platform-chip"
                      href={platform.profileUrl || '#'}
                      target={platform.profileUrl ? '_blank' : undefined}
                      rel={platform.profileUrl ? 'noreferrer' : undefined}
                    >
                      <span
                        className="user-profile-page__platform-icon"
                        style={{ color: platformMeta?.color || 'inherit' }}
                      >
                        {platformMeta?.icon || platform.platform.charAt(0).toUpperCase()}
                      </span>
                      <div className="user-profile-page__platform-text">
                        <span className="user-profile-page__platform-name">
                          {platformMeta?.name || platform.platform}
                        </span>
                        {platform.platformUsername && (
                          <span className="user-profile-page__platform-handle">
                            @{platform.platformUsername}
                          </span>
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {profile.mediaVisibility !== 'private' && isFollowing && profile.id && (
            <MediaGallery userId={profile.id} isOwnProfile={false} />
          )}

          {!profile.bio && !profile.videoUrl && platforms.length === 0 && (
            <div className="user-profile-page__empty-content">
              <p>This user hasn&apos;t added any public details yet.</p>
            </div>
          )}
        </section>
      </main>

      <FollowersPopup
        isOpen={showFollowersPopup}
        onClose={() => setShowFollowersPopup(false)}
        username={profile.username}
        initialTab={followersPopupTab}
      />

      <Footer fullWidth />
    </div>
  );
}

export default UserProfilePage;
